import React, { useEffect, useMemo, useState } from 'react';
import MathRenderer from '../math/MathRenderer';
import { questionAPI, SimilarQuestionResponse } from '../../services/api';
import { getUserMessage } from '../../utils/errors';

interface SimilarQuestionInput {
  id: string;
  label?: string;
}

interface SimilarQuestionPanelProps {
  questions: SimilarQuestionInput[];
  canGenerate: boolean;
}

const COOLDOWN_SECONDS = 120;
type GeneratedItem = {
  id: string;
  parts: {
    label: string;
    data: SimilarQuestionResponse;
  }[];
  revealed: boolean;
};

const formatCooldown = (seconds: number) => {
  const minutes = Math.floor(seconds / 60);
  const remaining = seconds % 60;
  return `${minutes}:${remaining.toString().padStart(2, '0')}`;
};

const getOptionKeys = (options?: Record<string, string | null> | null) => {
  if (!options) return [];
  return ['A', 'B', 'C', 'D', 'E'].filter((key) => options[key]);
};

const SimilarQuestionPanel: React.FC<SimilarQuestionPanelProps> = ({ questions, canGenerate }) => {
  const [generatedItems, setGeneratedItems] = useState<GeneratedItem[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cooldownUntil, setCooldownUntil] = useState<number | null>(null);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);

  const questionSignature = useMemo(
    () =>
      questions
        .map((question) => `${question.id}:${question.label ?? ''}`)
        .join('|'),
    [questions]
  );

  useEffect(() => {
    setGeneratedItems([]);
    setError(null);
    setIsGenerating(false);
    setCooldownUntil(null);
    setCooldownRemaining(0);
  }, [questionSignature]);

  useEffect(() => {
    if (!cooldownUntil) {
      setCooldownRemaining(0);
      return undefined;
    }

    const tick = () => {
      const remaining = Math.max(0, Math.ceil((cooldownUntil - Date.now()) / 1000));
      setCooldownRemaining(remaining);
      if (remaining <= 0) {
        setCooldownUntil(null);
      }
    };

    tick();
    const intervalId = window.setInterval(tick, 1000);
    return () => window.clearInterval(intervalId);
  }, [cooldownUntil]);

  const handleGenerate = async () => {
    if (!canGenerate || isGenerating || cooldownRemaining > 0) {
      return;
    }

    try {
      setIsGenerating(true);
      setError(null);
      const parts: GeneratedItem['parts'] = [];
      for (let index = 0; index < questions.length; index += 1) {
        const question = questions[index];
        const result = await questionAPI.generateSimilarQuestion(question.id);
        parts.push({
          label: question.label || `Part ${index + 1}`,
          data: result,
        });
      }
      const id = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      setGeneratedItems((prev) => [...prev, { id, parts, revealed: false }]);
      setCooldownUntil(Date.now() + COOLDOWN_SECONDS * 1000);
    } catch (err) {
      console.error('Failed to generate similar question', err);
      setError(getUserMessage(err, 'Unable to generate a similar question.'));
    } finally {
      setIsGenerating(false);
    }
  };

  const buttonLabel = useMemo(() => {
    if (!canGenerate) {
      return 'Generate similar question (Pro)';
    }
    if (isGenerating) {
      return 'Generating...';
    }
    if (cooldownRemaining > 0) {
      return `Generate again in ${formatCooldown(cooldownRemaining)}`;
    }
    return 'Generate similar question';
  }, [canGenerate, cooldownRemaining, isGenerating]);

  const handleToggleReveal = (itemId: string) => {
    setGeneratedItems((prev) =>
      prev.map((item) =>
        item.id === itemId ? { ...item, revealed: !item.revealed } : item
      )
    );
  };

  return (
    <div className="mt-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <button
          type="button"
          onClick={handleGenerate}
          disabled={!canGenerate || isGenerating || cooldownRemaining > 0}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-500 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed transition-colors"
        >
          {buttonLabel}
        </button>
        {!canGenerate && (
          <span className="text-xs text-gray-500 dark:text-gray-400">
            Available on paid plans.
          </span>
        )}
      </div>

      {error && (
        <div className="mt-3 px-4 py-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {generatedItems.map((item, index) => {
        return (
          <div
            key={item.id}
            className="mt-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 p-5 space-y-4"
          >
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Similar Question {index + 1}
              </h3>
              <button
                type="button"
                onClick={() => handleToggleReveal(item.id)}
                className="px-3 py-1 text-sm font-medium rounded-full border border-blue-200 text-blue-700 dark:text-blue-200 dark:border-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors"
              >
                {item.revealed ? 'Hide Solution' : 'Reveal Solution'}
              </button>
            </div>

            <div className="text-gray-900 dark:text-white">
              {item.parts.map((part, partIndex) => {
                const options = getOptionKeys(part.data.options);
                return (
                  <div key={`${item.id}-${partIndex}`} className="space-y-3">
                    <div className="text-sm font-semibold text-gray-700 dark:text-gray-200">
                      {part.label}
                    </div>
                    <MathRenderer latex={part.data.question_text} />

                    {options.length > 0 && (
                      <div className="space-y-2">
                        {options.map((key) => (
                          <div
                            key={`${part.label}-${key}`}
                            className="flex items-start gap-3 border border-gray-200 dark:border-gray-700 rounded-lg p-3"
                          >
                            <span className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-sm font-medium">
                              {key}
                            </span>
                            <div className="flex-1 text-gray-900 dark:text-white">
                              <MathRenderer latex={part.data.options?.[key] ?? ''} />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {item.revealed && (
                      <div className="space-y-3">
                        <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-700">
                          <div className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
                            Solution
                          </div>
                          <div className="text-gray-900 dark:text-white">
                            <MathRenderer latex={part.data.solution} />
                          </div>
                        </div>
                        <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-700">
                          <div className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
                            Worked Solution
                          </div>
                          <div className="text-gray-900 dark:text-white">
                            <MathRenderer latex={part.data.worked_solution} />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default SimilarQuestionPanel;
